// ** Modules
import React from 'react';

// ** Components
import ComponentBlocksHero from './Blocks/ComponentBlocksHero';
import ComponentBlocksImage from './Blocks/ComponentBlocksImage';
import ComponentBlocksEvents from './Blocks/ComponentBlocksEvents';
import ComponentBlocksAnchor from './Blocks/ComponentBlocksAnchor';
import ComponentBlocksCardsRow from './Blocks/ComponentBlocksCardsRow';
import ComponentBlocksNewsBlock from './Blocks/ComponentBlocksNewsBlock';
import ComponentBlocksTextBlock from './Blocks/ComponentBlocksTextBlock';
import ComponentBlocksFileBlock from './Blocks/ComponentBlocksFileBlock';
import ComponentBlocksTeamBlock from './Blocks/ComponentBlocksTeamBlock';
import ComponentBlocksVideoBlock from './Blocks/ComponentBlocksVideoBlock';
import ComponentBlocksButtonLink from './Blocks/ComponentBlocksButtonLink';
import ComponentDashboardOverview from './Dashboard/ComponentDashboardOverview';
import ComponentBlocksWysiwygBlock from './Blocks/ComponentBlocksWysiwygBlock';
import ComponentBlocksSectionBlock from './Blocks/ComponentBlocksSectionBlock';
import ComponentBlocksPodcastBlock from './Blocks/ComponentBlocksPodcastBlock';
import ComponentBlocksGraphicBlock from './Blocks/ComponentBlocksGraphicBlock';
import ComponentBlocksPartnerBlock from './Blocks/ComponentBlocksPartnerBlock';
import ComponentBlocksButtonLinkRow from './Blocks/ComponentBlocksButtonLinkRow';
import ComponentBlocksEhrenamtBlock from './Blocks/ComponentBlocksEhrenamtBlock';
import ComponentBlocksBildTextBlock from './Blocks/ComponentBlocksBildTextBlock';
import ComponentBlocksCarouselBlock from './Blocks/ComponentBlocksCarouselBlock';
import ComponentBlocksAccordionCards from './Blocks/ComponentBlocksAccordionCards';
import ComponentBlocksAccordionGroup from './Blocks/ComponentBlocksAccordionGroup';
import ComponentBlocksGridNavigation from './Blocks/ComponentBlocksGridNavigation';
import ComponentBlocksImageGridBlock from './Blocks/ComponentBlocksImageGridBlock';
import ComponentBlocksCompetitionCards from './Blocks/ComponentBlocksCompetitionCards';

// ** Context
import { useHeaderOverride } from '@/context/HeaderOverrideContext';

// ** Types
import type { User } from 'next-auth';
import type { Provider } from 'next-auth/providers';
import type { News, Page } from '@/types/graphql';
import type { AppContext } from '@/types/contextTypes';
import type { StrapiComponentProps } from '@/types/strapi';

type RenderableBlock = {
	__typename: string;
	id?: string | number;
};

type HeroBlock = NonNullable<Page['pageHero']> & {
	__typename: 'ComponentBlocksHero';
};

type BlockComponent = (
	| NonNullable<Page['pageContent']>[number]
	| NonNullable<News['newsContent']>[number]
	| HeroBlock
) &
	RenderableBlock;

const componentsMap: Record<string, React.ComponentType<StrapiComponentProps<any>>> = {
	ComponentBlocksHero,
	ComponentBlocksImage,
	ComponentBlocksEvents,
	ComponentBlocksAnchor,
	ComponentBlocksCardsRow,
	ComponentBlocksNewsBlock,
	ComponentBlocksTextBlock,
	ComponentBlocksFileBlock,
	ComponentBlocksTeamBlock,
	ComponentBlocksVideoBlock,
	ComponentBlocksButtonLink,
	ComponentDashboardOverview,
	ComponentBlocksWysiwygBlock,
	ComponentBlocksSectionBlock,
	ComponentBlocksPodcastBlock,
	ComponentBlocksGraphicBlock,
	ComponentBlocksPartnerBlock,
	ComponentBlocksEhrenamtBlock,
	ComponentBlocksBildTextBlock,
	ComponentBlocksButtonLinkRow,
	ComponentBlocksCarouselBlock,
	ComponentBlocksImageGridBlock,
	ComponentBlocksAccordionGroup,
	ComponentBlocksAccordionCards,
	ComponentBlocksGridNavigation,
	ComponentBlocksCompetitionCards
};

const NO_HERO_SPACING = 30;
/** Top padding for the first block after a hero section (responsive). */
const AFTER_HERO_SPACING = { xs: 10, md: 20 };
/** Bottom padding before the site footer (theme spacing units → 80px). */
const FOOTER_ABOVE_GAP_SPACING = 20;

type BlockConfig = {
	containerSx: Record<string, unknown>;
	extraProps?: object;
};

/**
 * Dynamic helper for vertical spacing and extra component props.
 */
const getComponentConfig = (
	typeName: string,
	extra: { newsBasePath?: string | null; eventsBasePath?: string | null }
): BlockConfig => {
	const configMap: Record<string, BlockConfig> = {
		// Background blocks — responsive padding inside their own colour
		ComponentBlocksCarouselBlock: {
			extraProps: {
				newsBasePath: extra.newsBasePath,
				eventsBasePath: extra.eventsBasePath
			},
			containerSx: { pt: { xs: 4, md: 20 }, pb: { xs: 10, md: 20 } }
		},
		ComponentBlocksEvents: {
			containerSx: { pt: { xs: 4, md: 20 }, pb: { xs: 4, md: 10 } }
		},
		ComponentBlocksButtonLinkRow: {
			containerSx: { py: { xs: 8, md: 20 } }
		},
		ComponentBlocksPartnerBlock: {
			containerSx: { py: { xs: 6, md: 10 } }
		},
		// Special / no-spacing blocks
		ComponentBlocksAccordionGroup: { containerSx: { py: 4 } },
		ComponentBlocksHero: { containerSx: { py: 0 } },
		// Default symmetric spacing for all other blocks
		default: { containerSx: { py: 10 } }
	};

	return configMap[typeName] || configMap.default;
};

// --- Helper: Dynamic Hero & Content Extraction ---
const getPageData = (data: Page | News | Array<Page | News>) => {
	const item = Array.isArray(data) ? data[0] : data;
	if (!item) return { hero: null, content: [], slug: '' };

	const hero = 'pageHero' in item ? item.pageHero : 'newsHero' in item ? (item as News).newsHero : null;
	const content =
		('pageContent' in item ? item.pageContent : 'newsContent' in item ? (item as News).newsContent : []) || [];

	return {
		hero,
		content,
		slug: item.slug || ''
	};
};

const StrapiPageRenderer = ({
	pageContent,
	locale,
	providers,
	currentUser,
	host,
	newsBasePath,
	eventsBasePath,
	userBasePath
}: {
	pageContent: Page | Page[] | News | News[];
	locale: AppContext['locale'];
	providers: Provider;
	currentUser: User | null | undefined;
	host: AppContext['host'];
	newsBasePath?: string | null;
	eventsBasePath?: string | null;
	userBasePath?: string | null;
}) => {
	const { setOverride } = useHeaderOverride();

	const { hero, content, slug } = getPageData(pageContent);

	React.useEffect(() => {
		if (!hero) {
			setOverride(null);
		}
	}, [hero, setOverride]);

	const componentBlocks: BlockComponent[] = [];
	if (hero) {
		componentBlocks.push({
			...hero,
			__typename: 'ComponentBlocksHero'
		} as BlockComponent);
	}

	componentBlocks.push(...(content as BlockComponent[]));

	return (
		<React.Fragment>
			{componentBlocks.map((item, index) => {
				const typeName = item?.__typename as string;
				if (!item || !typeName) return null;

				const Component = componentsMap[typeName];
				if (!Component) return null;

				// Pass props into the dynamic config helper
				const config = getComponentConfig(typeName, { newsBasePath, eventsBasePath });

				// Spacing Logic
				const isFirst = index === 0;
				const isLast = index === componentBlocks.length - 1;
				const isHero = typeName === 'ComponentBlocksHero';
				const isFirstAfterHero = index === 1 && componentBlocks[0]?.__typename === 'ComponentBlocksHero';

				const containerSx = {
					...config.containerSx,
					...(isFirst && !isHero && { pt: NO_HERO_SPACING }),
					...(isHero && { py: 0 }),
					...(isFirstAfterHero && { pt: AFTER_HERO_SPACING }),
					...(isLast && { pb: FOOTER_ABOVE_GAP_SPACING })
				};
				const extraProps = config.extraProps;

				const itemKey = 'id' in item ? `${typeName}_${item.id}_${locale}` : `${typeName}_${index}_${locale}`;

				return (
					<Component
						key={itemKey}
						locale={locale}
						providers={providers}
						currentUser={currentUser}
						host={host}
						slug={slug}
						containerSx={containerSx}
						newsBasePath={newsBasePath}
						eventsBasePath={eventsBasePath}
						userBasePath={userBasePath}
						extraProps={extraProps}
						{...item}
					/>
				);
			})}
		</React.Fragment>
	);
};

export default StrapiPageRenderer;
